import map from 'lodash/map';
import { formatUTC, validateDifferenceInMonths } from '@shared/utils/dateUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getTransfersByParams } from '@shared/services/api/transferReportService';

const useTransactionsReport = (search, pageSize, options = {}) => {
  const { autoFetch = true } = options;
  const [transactions, setTransactions] = useState(null);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState({});
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [sortConfig, setSortConfig] = useState({ sortBy: '', sortOrder: '' });
  const requestSeq = useRef(0);

  const buildTransactionList = (list) => {
    return map(list, (transaction) => ({
      ...transaction,
      amount: Number(transaction.amount).toFixed(2),
      transfer_date: formatUTC(transaction.transfer_date, 'dd-MM-yyyy'),
      transfer_hour: formatUTC(transaction.transfer_date, 'HH:mm'),
    }));
  };

  const fetchTransactions = useCallback(
    async (page = 0, size = rowsPerPage, sort = sortConfig, searchOverride = undefined) => {
      const criteria = searchOverride ?? search;

      try {
        const { date_from: start, date_to: end } = criteria;
        if (validateDifferenceInMonths(start, end, 3)) {
          setError({});
        }
      } catch (e) {
        if (e?.message?.toString().includes('Rango de fechas excedido:')) {
          setError((prev) => ({ ...prev, date: { error: true, helperText: e.message } }));
        }
        return;
      }

      const seq = ++requestSeq.current;
      setIsLoading(true);
      try {
        const response = await getTransfersByParams(criteria, 0, page, size, false, sort.sortBy, sort.sortOrder);
        if (seq !== requestSeq.current) {
          return;
        }
        const { data_set, rows_count } = response?.data || {};
        setTransactions(buildTransactionList(data_set));
        setCount(parseInt(rows_count));
        setCurrentPage(page);
      } finally {
        if (seq === requestSeq.current) {
          setIsLoading(false);
        }
      }
    },
    [search, rowsPerPage, sortConfig]
  );

  const fetchRef = useRef(fetchTransactions);
  fetchRef.current = fetchTransactions;

  const handleChangeRowsPerPage = (page, size) => {
    const pageSize = Number(size);
    setRowsPerPage(pageSize);
    setCurrentPage(0);
    fetchTransactions(0, pageSize);
  };

  const handleSortChange = useCallback(
    ({ property, order }) => {
      const newSort = { sortBy: property, sortOrder: order.toUpperCase() };
      setSortConfig(newSort);
      setCurrentPage(0);
      fetchTransactions(0, rowsPerPage, newSort);
    },
    [fetchTransactions, rowsPerPage]
  );

  useEffect(() => {
    if (!autoFetch) {
      return undefined;
    }

    try {
      const { date_from: start, date_to: end } = search;
      if (validateDifferenceInMonths(start, end, 3)) {
        setError({});
        fetchRef.current(0);
      }
    } catch (e) {
      if (e?.message?.toString().includes('Rango de fechas excedido:')) {
        setError((prev) => ({ ...prev, date: { error: true, helperText: e.message } }));
      }
    }

    return undefined;
  }, [search, autoFetch]);

  return {
    transactions,
    count,
    currentPage,
    fetchTransactions,
    isLoading,
    error,
    handleChangeRowsPerPage,
    sortConfig,
    handleSortChange,
  };
};

export default useTransactionsReport;
